import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { StandardAccountDictionaryProposal } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import {
  currentItemsWithHighlight,
  proposalKindSection,
  proposedItemText,
  reviewKindLabel,
  statementTypeLabel,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';

/**
 * Uma linha da lista "A revisar" da aba Dicionário (task 3452): proposta do revisor que já entrou sozinha no
 * dicionário. Numa altura só mostra a conta padrão, o tipo, o texto acrescentado (até duas linhas) e as ações Manter e
 * Desfazer; clicar abre o motivo do revisor, a linha que motivou e a seção do verbete atual. Só apresentação: as ações
 * saem por `output()`.
 */
@Component({
  selector: 'app-dictionary-review-row',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './dictionary-review-row.component.html',
  styleUrl: './dictionary-review-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryReviewRowComponent {
  readonly proposal = input.required<StandardAccountDictionaryProposal>();
  readonly expanded = input(false);
  /** Pode manter e desfazer (permissão MANAGE). */
  readonly canManage = input(false);
  /** Esta linha está sendo mantida ou desfeita agora. */
  readonly busy = input(false);
  /** Outra ação do painel está em andamento: trava os botões. */
  readonly locked = input(false);

  readonly toggleRequested = output<void>();
  readonly keepRequested = output<StandardAccountDictionaryProposal>();
  readonly revertRequested = output<StandardAccountDictionaryProposal>();

  protected readonly kindLabel = computed(() => reviewKindLabel(this.proposal()));
  protected readonly statementLabel = computed(() =>
    statementTypeLabel(this.proposal().statementType)
  );
  /** Texto acrescentado; no "Não entra" o destino já está na tag, então aqui vai só o item. */
  protected readonly text = computed(() => {
    const proposal = this.proposal();
    return proposal.kind === 'Exclude' ? (proposal.text ?? '').trim() : proposedItemText(proposal);
  });
  protected readonly path = computed(() => this.proposal().currentEntry?.path?.trim() || null);
  protected readonly sectionLabel = computed(() => proposalKindSection(this.proposal().kind));
  protected readonly currentItems = computed(() => currentItemsWithHighlight(this.proposal()));

  protected onMainKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggleRequested.emit();
  }
}
